import { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuthStore } from '../stores/authStore';
import { UserSubscriptionStatus, SubscriptionPlan } from '../services/subscriptionService';

// Backward-compat alias — any file that imported UserSubscription from here still compiles
export type UserSubscription = UserSubscriptionStatus;

// ── Free-plan fallback (mirrors DB row 79684cb1) ──────────────────────────────
const FREE_LIMITS: SubscriptionPlan['limits'] = {
  credits: 5,
  export: false,
  pdf_export: false,
  excel_export: false,
  cloud_sync: false,
  advanced_analytics: false,
  ai_reports: false,
  priority_support: false,
  api_access: false,
  team_size: 1,
  custom_integrations: false,
  white_label: false,
  sla: false,
};

const FREE_PLAN: UserSubscriptionStatus = {
  plan_name: 'free',
  plan_display_name: 'Free',
  status: 'active',
  limits: FREE_LIMITS,
  features: ['5 AI queries per month', 'Basic loan calculators', 'Local history only', 'Standard support'],
};

// ── localStorage helpers ──────────────────────────────────────────────────────
const storageKey = (userId?: string) => `sub_v2_${userId}`;

const saveToStorage = (userId: string, sub: UserSubscriptionStatus) => {
  try { localStorage.setItem(storageKey(userId), JSON.stringify(sub)); } catch {}
};

const getFromStorage = (userId?: string): UserSubscriptionStatus | null => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed: UserSubscriptionStatus = JSON.parse(raw);
    // Invalidate stale cache entries that are missing limits/features
    if (!parsed.limits || !parsed.features) return null;
    return parsed;
  } catch { return null; }
};

// ── DB fetch — selects limits + features from joined plan row ─────────────────
const fetchFromDB = async (userId: string): Promise<UserSubscriptionStatus> => {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(`
        status, expires_at, billing_cycle, plan_id,
        subscription_plans!plan_id(name, display_name, features, limits)
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error || !data) return FREE_PLAN;

    const plan = data.subscription_plans as any;
    if (!plan) return FREE_PLAN;

    return {
      plan_name: plan.name ?? 'free',
      plan_display_name: plan.display_name ?? 'Free',
      status: data.status,
      expires_at: data.expires_at ?? undefined,
      billing_cycle: data.billing_cycle ?? undefined,
      limits: plan.limits ?? FREE_LIMITS,
      features: plan.features ?? [],
    };
  } catch {
    return FREE_PLAN;
  }
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export const useSubscription = () => {
  const { user } = useAuthStore();
  const userId = user?.id;
  const fetchingRef = useRef(false);

  const [subscription, setSubscription] = useState<UserSubscriptionStatus | null>(
    () => getFromStorage(userId)
  );
  const [loading, setLoading] = useState(() => !getFromStorage(userId));

  const refresh = async () => {
    if (!userId) { setSubscription(FREE_PLAN); setLoading(false); return; }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const sub = await fetchFromDB(userId);
      saveToStorage(userId, sub);
      setSubscription(sub);
    } catch {
      setSubscription(FREE_PLAN);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Reset + reload when user changes
  useEffect(() => {
    fetchingRef.current = false;
    const cached = getFromStorage(userId);
    setSubscription(cached);
    setLoading(!cached);
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Re-fetch on payment / subscription events
  useEffect(() => {
    const handle = () => {
      if (userId) { try { localStorage.removeItem(storageKey(userId)); } catch {} }
      fetchingRef.current = false;
      refresh();
    };
    window.addEventListener('payment-success', handle);
    window.addEventListener('subscriptionUpdated', handle);
    window.addEventListener('creditsUpdated', handle);
    return () => {
      window.removeEventListener('payment-success', handle);
      window.removeEventListener('subscriptionUpdated', handle);
      window.removeEventListener('creditsUpdated', handle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const planName = subscription?.plan_name?.toLowerCase() ?? 'free';
  const isPremium = planName === 'premium';
  const isPro     = planName === 'pro';
  const isBasic   = planName === 'basic';
  const isFree    = !isPremium && !isPro && !isBasic;

  return {
    subscription,
    loading,
    isPremium,
    isPro,
    isBasic,
    isFree,
    refetch: refresh,
    forceRefresh: refresh,
  };
};
